import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Card, CardTitle, Field, Input, Select, Toggle } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import { MODELS, updateConfigSchema, type Config, type UpdateConfigInput } from '../schemas';
import { useUpdateConfig } from '../api';

interface Props {
  config: Config;
}

export function ConfigForm({ config }: Props) {
  const update = useUpdateConfig();
  const form = useForm<UpdateConfigInput>({
    resolver: zodResolver(updateConfigSchema),
    defaultValues: config,
  });

  useEffect(() => {
    form.reset(config);
  }, [config, form]);

  const onSubmit = form.handleSubmit((values) => {
    update.mutate(values, { onSuccess: () => form.reset(values) });
  });

  return (
    <Card padding="lg">
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <CardTitle>Configurações do Hub</CardTitle>
        <Stack gap="md">
          <Field
            label="Modelo padrão"
            htmlFor="cfg-model"
            error={form.formState.errors.defaultModel?.message}
          >
            <Select id="cfg-model" {...form.register('defaultModel')}>
              {MODELS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
          </Field>

          <ToggleField
            label="Light context"
            description="Reduz o tamanho do contexto enviado aos agentes (mais barato, menos preciso)."
            value={form.watch('lightContext')}
            onChange={(v) => form.setValue('lightContext', v, { shouldDirty: true })}
          />

          <ToggleField
            label="Reasoning"
            description="Habilita modo de raciocínio prolongado (extended thinking) quando suportado."
            value={form.watch('reasoning')}
            onChange={(v) => form.setValue('reasoning', v, { shouldDirty: true })}
          />

          <div className="rounded-md border border-border bg-surface p-3 flex flex-col gap-3">
            <ToggleField
              label="Heartbeat"
              description="Pulso periódico para os agentes — habilita tarefas proativas."
              value={form.watch('heartbeat.enabled')}
              onChange={(v) => form.setValue('heartbeat.enabled', v, { shouldDirty: true })}
            />
            <Field
              label="Intervalo (minutos)"
              htmlFor="cfg-heartbeat-interval"
              hint="Entre 5 e 480 minutos."
              error={form.formState.errors.heartbeat?.intervalMinutes?.message}
            >
              <Input
                id="cfg-heartbeat-interval"
                type="number"
                min={5}
                max={480}
                disabled={!form.watch('heartbeat.enabled')}
                {...form.register('heartbeat.intervalMinutes', { valueAsNumber: true })}
              />
            </Field>
          </div>
        </Stack>

        {update.error && (
          <p className="text-xs text-danger">
            {update.error instanceof ApiError ? update.error.message : 'Falha ao salvar'}
          </p>
        )}
        {update.isSuccess && !form.formState.isDirty && (
          <p className="text-xs text-ok">Configuração salva.</p>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="submit"
            variant="primary"
            disabled={update.isPending || !form.formState.isDirty}
          >
            {update.isPending ? 'Salvando…' : 'Salvar configuração'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

interface ToggleFieldProps {
  label: string;
  description: string;
  value: boolean;
  onChange: (v: boolean) => void;
}

function ToggleField({ label, description, value, onChange }: ToggleFieldProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-sm font-medium text-text">{label}</span>
        <span className="text-xs text-text-2">{description}</span>
      </div>
      <Toggle checked={value} onCheckedChange={onChange} aria-label={label} />
    </div>
  );
}
